import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { RefreshCw } from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeToggle } from "./components/ThemeToggle";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { VersionSentinel } from "./components/VersionSentinel";
import { WorkspaceTabRoutes } from "./components/WorkspaceTabRoutes";
import { getLandingPage } from "./lib/uiPrefs";
import { HUBS } from "./lib/hubs";

// c61_809 — every page is its own chunk. A partner opening /portal/… (or a vendor share link)
// downloads the portal code only, not the whole Snagger workspace (32 MB in dev, several MB built).
const RepriceDashboard = lazy(() => import("./pages/RepriceDashboard"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const ChannelHub = lazy(() => import("./pages/ChannelHub"));
const McrHub = lazy(() => import("./pages/McrHub"));
const BooksHub = lazy(() => import("./pages/BooksHub"));
const PlaidOAuthReturn = lazy(() => import("./pages/PlaidOAuthReturn"));
const FBARestock = lazy(() => import("./pages/FBARestock"));
const AdsHub = lazy(() => import("./pages/AdsHub"));
const DropShipHub = lazy(() => import("./pages/DropShipHub"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const EulaPage = lazy(() => import("./pages/EulaPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const MyTime = lazy(() => import("./pages/MyTime"));
const WarehousePerformancePage = lazy(() => import("./pages/WarehousePerformancePage"));
const WarehousePerformanceSharePage = lazy(() => import("./pages/WarehousePerformancePage").then((m) => ({ default: m.WarehousePerformanceSharePage })));
const MapWatchSharePage = lazy(() => import("./pages/MapWatchSharePage"));
const PortalPage = lazy(() => import("./portal/PortalPage"));
const AttestPage = lazy(() => import("./portal/AttestPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));

const Spinner = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center" data-testid="app-chunk-loading">
    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
  </div>
);

// c60_15_phase_3i — Public pages reachable WITHOUT login. Required by
// third-party app-registration flows (e.g. Intuit Developer for QBO
// production) that expect public EULA / Privacy URLs to return real
// HTML.
const PUBLIC_PATHS = new Set(["/eula", "/privacy", "/my-time"]);
// c61_550 — vendor share links open without a login; c61_805 — partner portal (email-code gate)
const isPublicPath = (p) => PUBLIC_PATHS.has(p) || p.startsWith("/share/warehouse/") || p.startsWith("/share/map-watch/") || p.startsWith("/portal/") || p.startsWith("/attest/");

function ProtectedApp() {
  const { user, loading } = useAuth();
  const location = useLocation();
  useVersionCheck();

  // Short-circuit BEFORE the auth gate so the legal pages render
  // even for signed-out visitors (and never trigger a login redirect).
  if (isPublicPath(location.pathname)) {
    return (
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/eula" element={<EulaPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/my-time" element={<MyTime />} />
          <Route path="/share/warehouse/:token" element={<WarehousePerformanceSharePage />} />
          <Route path="/share/map-watch/:token" element={<MapWatchSharePage />} />
          <Route path="/portal/:token" element={<PortalPage />} />
          <Route path="/attest/:token" element={<AttestPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (loading) return <Spinner />;

  if (!user) { landingTarget(null); return <Suspense fallback={<Spinner />}><LoginPage /></Suspense>; }

  // c61_797 — per-account start page, decided BEFORE the routes mount (the Business Hub writes
  // its own "?tab=…" on mount and would overwrite a redirect issued afterwards)
  const landing = landingTarget(user, location);
  if (landing) return <Navigate to={landing} replace />;

  // c61_673 — every workspace tab keeps its own live page (see WorkspaceTabRoutes)
  return <Suspense fallback={<Spinner />}><WorkspaceTabRoutes>{ROUTES}</WorkspaceTabRoutes></Suspense>;
}

// Once per browser session (flag cleared on logout): when the app is OPENED at the root — not a
// deep link — return the page this account picked; otherwise null and the routes render as-is.
// Decided once per page load and remembered (render runs twice under StrictMode), handed out only
// while still at "/", then dropped so later in-app trips to "/" stay put.
let landingDecision;
function landingTarget(user, location) {
  if (!user?.username) { landingDecision = undefined; return null; }
  if (landingDecision === undefined) {
    if (sessionStorage.getItem("ss_landed") === "1") landingDecision = null;
    else {
      sessionStorage.setItem("ss_landed", "1");
      // c61_867 — an account without the Business Hub (e.g. FTMD-only) opens on the first hub it may see
      const perms = user.permissions || [];
      const canDash = user.role === "admin" || perms.includes("dashboard");
      const firstHub = canDash ? "/"
        : (HUBS.find((h) => perms.includes(h.id) || (h.id === "connection_hub" && perms.includes("channel_hub")))?.path || "/");
      // getLandingPage() answers "/" when nothing was picked — for an account without the Business Hub that means "unset"
      const stored = user.ui_prefs?.landing_page || getLandingPage(user.username) || "";
      const target = stored && (stored !== "/" || canDash) ? stored : firstHub;
      const params = new URLSearchParams(location.search);
      params.delete("tab");
      const atRoot = location.pathname === "/" && params.toString() === "";
      landingDecision = atRoot && target !== "/" ? target : null;
    }
  }
  if (landingDecision && location.pathname === "/") return landingDecision;
  landingDecision = null;
  return null;
}

// c61_867 — a hub page only renders for an account holding its permission; otherwise a plain
// "no access" page naming the hubs the account does have (the API already answers 403, this stops
// the empty-looking shell — e.g. an FTMD-only bookkeeper opening /books).
const HubGate = ({ perm, children }) => {
  const { user, hasPermission } = useAuth();
  if (hasPermission(perm)) return children;
  const mine = HUBS.filter((h) => hasPermission(h.id));
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-6" data-testid="hub-no-access">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-3">
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">No access to this hub</div>
        <p className="text-xs text-gray-500">Your account ({user?.username}) does not hold the <code>{perm}</code> permission. Ask an admin in User Management if you need it.</p>
        {mine.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {mine.map((h) => <a key={h.id} href={h.path} className="text-xs px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`hub-no-access-go-${h.id}`}>{h.label} {h.sub}</a>)}
          </div>
        )}
      </div>
    </div>
  );
};

// Static so the per-tab <Routes> trees only re-render on navigation, never on tab-strip bookkeeping.
const ROUTES = (
  <>
    <Route path="/" element={<HubGate perm="dashboard"><DashboardPage /></HubGate>} />
    <Route path="/channel-hub" element={<HubGate perm="channel_hub"><McrHub /></HubGate>} />
    <Route path="/books" element={<HubGate perm="books_hub"><BooksHub /></HubGate>} />
    <Route path="/ftmd" element={<HubGate perm="ftmd"><BooksHub company="ftmd" /></HubGate>} />
    <Route path="/personal" element={<HubGate perm="personal"><BooksHub company="personal" /></HubGate>} />
    <Route path="/plaid-oauth" element={<PlaidOAuthReturn />} />
    <Route path="/connection-hub" element={<HubGate perm="connection_hub"><ChannelHub /></HubGate>} />
    <Route path="/restock" element={<HubGate perm="sales"><FBARestock /></HubGate>} />
    <Route path="/reprice" element={<HubGate perm="reprice"><RepriceDashboard /></HubGate>} />
    <Route path="/reprice/oauth/callback" element={<RepriceDashboard />} />
    <Route path="/ads" element={<HubGate perm="ads"><AdsHub /></HubGate>} />
    <Route path="/dropship" element={<HubGate perm="dropship"><DropShipHub /></HubGate>} />
    <Route path="/dropship/:section" element={<HubGate perm="dropship"><DropShipHub /></HubGate>} />
    <Route path="/dropship/:section/:sub" element={<HubGate perm="dropship"><DropShipHub /></HubGate>} />
    <Route path="/warehouse-performance" element={<WarehousePerformancePage />} />
    <Route path="/settings" element={<SettingsPage />} />
    <Route path="/admin" element={<AdminPage />} />
    <Route path="/partners" element={<PartnersPage />} />
    <Route path="/feedback" element={<FeedbackPage />} />
    {/* c61_11 — unknown paths (e.g. a bookmarked /login) render
        blank once authenticated; send them home instead. */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ProtectedApp />
        </BrowserRouter>
      </AuthProvider>
      {/* c61_237 — toasts now rise from the BOTTOM (user request: the
          top toasts covered the header/hub controls). bottom-center
          keeps clear of the ThemeToggle + VersionSentinel cluster in
          the bottom-right corner. ``expand`` lets multiple toasts
          stack visibly without collapsing into a single tile. */}
      <Toaster
        position="bottom-center"
        richColors
        expand
        offset={24}
        toastOptions={{ duration: 4000 }}
      />
      {!/^\/(portal|attest)\//.test(window.location.pathname) && <ThemeToggle />}
      {!/^\/(portal|attest)\//.test(window.location.pathname) && <VersionSentinel />}
    </div>
  );
}

export default App;
